import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "react-query";
import { HelmetProvider } from "react-helmet-async";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { Toaster } from "react-hot-toast";
import { ThemeProvider } from "./contexts/ThemeContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { SubscriptionProvider } from "./hooks/useSubscription";
import { NotificationProvider } from "./hooks/useNotifications";
import Layout from "./components/Layout/Layout";
import Dashboard from "./components/Dashboard/Dashboard";
import GeneralPage from "./pages/GeneralPage";
import AppearancePage from "./pages/AppearancePage";
// import AdvancedPage from "./pages/AdvancedPage";
import IntegrationPage from "./pages/IntegrationPage";
import BillingPage from "./pages/BillingPagePaddle";
import SourcesPage from "./pages/SourcesPage";
import KnowledgeBasePage from "./pages/KnowledgeBasePage";
import LoginPage from "./components/Auth/LoginPage";
import RegisterPage from "./components/Auth/RegisterPage";
import ForgotPasswordPage from "./components/Auth/ForgotPasswordPage";
import ResetPasswordPage from "./components/Auth/ResetPasswordPage";
import VerifyEmailPage from "./components/Auth/VerifyEmailPage";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import RefundPolicy from "./pages/RefundPolicy";
import CookiePolicy from "./pages/CookiePolicy";
import NotificationsPage from "./pages/NotificationsPage";
import LandingPage from "./pages/LandingPage";
import PricingPage from "./pages/PricingPage";
import SecurityPage from "./pages/SecurityPage";
import PlatformPage from "./pages/PlatformPage";
import { PLATFORM_PAGES } from "./config/platformPages";
import NotFound from "./pages/NotFound";
import CookieNotice from "./components/CookieNotice";
import { LanguageProvider } from "./i18n/LanguageContext";
import "./index.css";

// Create React Query client
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: (failureCount, error) => {
                // Never retry subscription/auth errors
                if (error?.isSubscriptionRequired || error?.status === 403 || error?.status === 401) return false;
                return failureCount < 1;
            },
            refetchOnWindowFocus: false,
            staleTime: 1000 * 60 * 5, // 5 minutes
        },
    },
});

// Scroll to top on route change
function ScrollToTop() {
    const { pathname } = useLocation();
    React.useEffect(() => {
        window.scrollTo(0, 0);
        // Also scroll the dashboard main content area (lg:overflow-auto)
        const main = document.querySelector('main');
        if (main) main.scrollTop = 0;
    }, [pathname]);
    return null;
}

// Protected Route component
function ProtectedRoute({ children }) {
    const { isAuthenticated, isLoading, isInitialized } = useAuth();

    if (!isInitialized || isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="flex flex-col items-center space-y-4">
                    <div className="w-12 h-12 border-3 border-gray-200 dark:border-gray-700 border-t-black dark:border-t-white rounded-full animate-spin"></div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-primary mb-1">Loading PaperBrainz</h3>
                        <p className="text-sm text-secondary">Initializing your dashboard...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    return children;
}

// Public Route component (redirect to dashboard if authenticated)
function PublicRoute({ children }) {
    const { isAuthenticated, isLoading, isInitialized } = useAuth();

    if (!isInitialized || isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="flex flex-col items-center space-y-4">
                    <div className="w-12 h-12 border-3 border-gray-200 dark:border-gray-700 border-t-black dark:border-t-white rounded-full animate-spin"></div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-primary mb-1">Loading PaperBrainz</h3>
                        <p className="text-sm text-secondary">Checking authentication...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (isAuthenticated) {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
}

function App() {
    return (
        <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || "401411256876-dd8oqmmrurrifbua413f5qinmpe7s09a.apps.googleusercontent.com"}>
        <HelmetProvider>
            <QueryClientProvider client={queryClient}>
                <AuthProvider>
                    <SubscriptionProvider>
                        <NotificationProvider>
                            <ThemeProvider>
                                <Router>
                                <LanguageProvider>
                                <ScrollToTop />
                                <div className="App">
                                    <Routes>
                                        {/* Landing Page */}
                                        <Route path="/" element={<LandingPage />} />
                                        <Route path="/hy" element={<LandingPage />} />

                                        {/* Public routes */}
                                        <Route
                                            path="/login"
                                            element={
                                                <PublicRoute>
                                                    <LoginPage />
                                                </PublicRoute>
                                            }
                                        />
                                        <Route
                                            path="/register"
                                            element={
                                                <PublicRoute>
                                                    <RegisterPage />
                                                </PublicRoute>
                                            }
                                        />
                                        <Route
                                            path="/forgot-password"
                                            element={
                                                <PublicRoute>
                                                    <ForgotPasswordPage />
                                                </PublicRoute>
                                            }
                                        />
                                        <Route
                                            path="/reset-password"
                                            element={
                                                <PublicRoute>
                                                    <ResetPasswordPage />
                                                </PublicRoute>
                                            }
                                        />
                                        <Route path="/verify-email" element={<VerifyEmailPage />} />

                                        {/* Public marketing pages — each separately indexable */}
                                        <Route path="/pricing" element={<PricingPage />} />
                                        <Route path="/security" element={<SecurityPage />} />
                                        {PLATFORM_PAGES.map((p) => (
                                            <Route key={p.slug} path={p.path} element={<PlatformPage slug={p.slug} />} />
                                        ))}

                                        {/* Legal pages */}
                                        <Route path="/terms-of-service" element={<TermsOfService />} />
                                        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                                        <Route path="/refund-policy" element={<RefundPolicy />} />
                                        <Route path="/cookie-policy" element={<CookiePolicy />} />

                                        {/* Protected routes */}
                                        <Route
                                            path="/dashboard/*"
                                            element={
                                                <ProtectedRoute>
                                                    <Layout>
                                                        <Routes>
                                                            <Route path="*" element={<Dashboard />} />
                                                            <Route path="/" element={<Dashboard />} />
                                                            <Route path="/general" element={<GeneralPage />} />
                                                            <Route path="/appearance" element={<AppearancePage />} />
                                                            {/* <Route path="/advanced" element={<AdvancedPage />} /> */}
                                                            <Route path="/integration" element={<IntegrationPage />} />
                                                            <Route path="/billing" element={<BillingPage />} />
                                                            <Route path="/sources" element={<SourcesPage />} />
                                                            <Route path="/knowledge-base" element={<KnowledgeBasePage />} />
                                                            <Route path="/notifications" element={<NotificationsPage />} />
                                                        </Routes>
                                                    </Layout>
                                                </ProtectedRoute>
                                            }
                                        />

                                        {/* 404 — must be last */}
                                        <Route path="*" element={<NotFound />} />
                                    </Routes>

                                    <CookieNotice />

                                    {/* Toast notifications */}
                                    <Toaster
                                        position="top-right"
                                        toastOptions={{
                                            duration: 2000,
                                            className: "toast-custom cursor-pointer",
                                            style: {
                                                borderRadius: "8px",
                                                fontSize: "14px",
                                                padding: "12px 16px",
                                            },
                                        }}
                                    />
                                </div>
                                </LanguageProvider>
                                </Router>
                            </ThemeProvider>
                        </NotificationProvider>
                    </SubscriptionProvider>
                </AuthProvider>
            </QueryClientProvider>
        </HelmetProvider>
        </GoogleOAuthProvider>
    );
}

export default App;
